import React, { useState, useEffect, useRef } from 'react';
import { useGame, api } from '../context/GameContext';
import { Sparkles } from 'lucide-react';

// Onboarding-specific i18n. Falls back to English for any missing locale.
// Keep keys here (rather than translations.js) because they're tightly
// coupled to this single component and would otherwise pollute the global
// dictionary.
const ONBOARDING_T = {
  welcome: {
    en: 'Welcome!', ru: 'Добро пожаловать!', uk: 'Ласкаво просимо!',
    es: '¡Bienvenido!', fr: 'Bienvenue !', pt: 'Bem-vindo!',
    ar: 'مرحباً!', zh: '欢迎！',
  },
  continue: {
    en: 'Continue', ru: 'Продолжить', uk: 'Продовжити',
    es: 'Continuar', fr: 'Continuer', pt: 'Continuar',
    ar: 'متابعة', zh: '继续',
  },
  tap_to_reveal: {
    en: 'Tap to reveal', ru: 'Нажмите, чтобы открыть', uk: 'Торкніться, щоб відкрити',
    es: 'Toca para descubrir', fr: 'Touchez pour révéler', pt: 'Toque para revelar',
    ar: 'انقر للكشف', zh: '点击打开',
  },
  tap_painting_hint: {
    en: '👆 Tap the painting', ru: '👆 Нажмите на картину', uk: '👆 Торкніться картини',
    es: '👆 Toca el cuadro', fr: '👆 Touchez le tableau', pt: '👆 Toque na pintura',
    ar: '👆 انقر على اللوحة', zh: '👆 点击画作',
  },
  claim_painting: {
    en: 'Claim Painting', ru: 'Забрать картину', uk: 'Забрати картину',
    es: 'Reclamar cuadro', fr: 'Réclamer le tableau', pt: 'Reclamar pintura',
    ar: 'احصل على اللوحة', zh: '领取画作',
  },
  claiming: {
    en: 'Claiming...', ru: 'Добавляем...', uk: 'Додаємо...',
    es: 'Reclamando...', fr: 'Réclamation...', pt: 'A reclamar...',
    ar: 'جارٍ الحصول...', zh: '添加中...',
  },
  claimed: {
    en: 'Claimed!', ru: 'Добавлено!', uk: 'Додано!',
    es: '¡Reclamado!', fr: 'Réclamé !', pt: 'Reclamado!',
    ar: 'تم الحصول!', zh: '已添加！',
  },
  err_generic: {
    en: 'Failed to claim painting',
    ru: 'Не удалось получить картину',
    uk: 'Не вдалося отримати картину',
    es: 'No se pudo reclamar el cuadro',
    fr: 'Échec de la réclamation du tableau',
    pt: 'Não foi possível reclamar a pintura',
    ar: 'فشل الحصول على اللوحة',
    zh: '领取失败',
  },
  intro_lead: {
    en: 'Your first masterpiece is waiting',
    ru: 'Ваш первый шедевр ждёт',
    uk: 'Ваш перший шедевр чекає',
    es: 'Tu primera obra maestra te espera',
    fr: 'Votre premier chef-d\u2019\u0153uvre vous attend',
    pt: 'Sua primeira obra-prima aguarda',
    ar: 'تحفتك الأولى بانتظارك',
    zh: '您的第一幅杰作正在等待',
  },
  err_no_cards: {
    en: 'No paintings available yet. Try again later!',
    ru: 'Пока нет доступных картин. Попробуйте позже!',
    uk: 'Поки немає доступних картин. Спробуйте пізніше!',
    es: 'Aún no hay cuadros disponibles. ¡Inténtalo más tarde!',
    fr: 'Aucun tableau disponible pour le moment. Réessayez plus tard !',
    pt: 'Ainda não há pinturas disponíveis. Tente mais tarde!',
    ar: 'لا توجد لوحات متاحة بعد. حاول لاحقاً!',
    zh: '暂无可用画作。请稍后再试！',
  },
};
const tr = (key, lang) => (ONBOARDING_T[key] && (ONBOARDING_T[key][lang] || ONBOARDING_T[key].en)) || key;

const RARITY_COLORS = {
  common: '#9CA3AF',
  uncommon: '#93C5FD',
  rare: '#3B82F6',
  legendary: '#A855F7',
  masterpiece: '#D4AF37',
};
const RARITY_LABELS = {
  common: { en: 'Common', ru: 'Обычная', zh: '普通' },
  uncommon: { en: 'Uncommon', ru: 'Необычная', zh: '非凡' },
  rare: { en: 'Rare', ru: 'Редкая', zh: '稀有' },
  legendary: { en: 'Legendary', ru: 'Легендарная', zh: '传奇' },
  masterpiece: { en: 'Masterpiece', ru: 'Шедевр', zh: '杰作' },
};

const OnboardingCard = ({ onComplete }) => {
  const { user, t, refreshUser, language, haptic } = useGame();
  const [stage, setStage] = useState('appearing'); // appearing, revealed, claiming, done, fading
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  // Pre-fetched candidate. Populated as soon as the component mounts so
  // that by the time the player taps the face-down card the painting
  // payload + image are already in memory and the flip reveal is
  // instant (no "loading" flash on first session — that was the
  // single most-complained-about onboarding moment).
  const prefetchRef = useRef(null);

  useEffect(() => {
    // Animate card appearing
    const timer = setTimeout(() => {
      setStage('hidden');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Prefetch the painting + its image the moment the onboarding overlay
  // mounts. Stored in a ref so the click handler can use it
  // synchronously. The browser holds the decoded bitmap in its image
  // cache, so reusing the same `image_url` in the reveal renders
  // without re-fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/cards/preview-random');
        if (cancelled || !response.data) return;
        const payload = response.data;
        prefetchRef.current = payload;
        // Force the browser to actually fetch+decode the bitmap so the
        // first paint after flip is hot from cache. We don't `await`
        // this — the Image() object decodes on its own; what matters
        // is that the request is in flight.
        if (payload?.image_url) {
          const img = new window.Image();
          img.decoding = 'async';
          img.src = payload.image_url;
        }
      } catch (_) { /* silent — falls back to lazy fetch in handleCardClick */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCardClick = async () => {
    if (stage === 'hidden') {
      haptic();
      // Use the prefetched candidate if it's ready (the common case
      // — the prefetch starts on mount). Falls back to a fresh fetch
      // if the network was slow on the prefetch.
      if (prefetchRef.current) {
        setCard(prefetchRef.current);
        setStage('revealed');
        return;
      }
      try {
        const response = await api.get('/cards/preview-random');
        setCard(response.data);
        setStage('revealed');
      } catch (err) {
        console.error('Error fetching card:', err);
        if (err.response?.status === 404) {
          setError(tr('err_no_cards', language));
        } else {
          setError(tr('err_generic', language));
        }
        setStage('no-cards');
      }
    }
  };

  const handleClaim = async () => {
    if (!card || !user?.id || stage !== 'revealed') return;

    haptic();
    setStage('claiming');
    // Fire the server call FIRST and wait for the real card payload
    // (with the real `id`, `durability=50` for the tutorial, owner_id,
    // etc.). Without this the optimistic flow handed the gallery a
    // preview-shaped payload (template) and the modal opened with the
    // default 100% durability, breaking the Repair tutorial step.
    let claimed = null;
    try {
      const res = await api.post(`/cards/claim-first?template_id=${card.template_id || card.id}`);
      claimed = res.data?.card || null;
    } catch (err) {
      console.error('Error claiming card:', err);
      try {
        const fb = await api.post('/cards/buy-random?tutorial=true');
        claimed = fb.data?.card || null;
      } catch (fallbackErr) {
        setError('Could not claim card');
        setStage('revealed');
        return;
      }
    }
    // Refresh user state in the background — the gallery's loadCards
    // will also refresh, but the balance pill reads from `user.coins`
    // independently so triggering this here keeps it in sync.
    refreshUser?.();
    setStage('done');
    setTimeout(() => setStage('fading'), 500);
    setTimeout(() => onComplete(claimed || card), 900);
  };

  const skipOnboarding = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-[#05050A] z-50 flex flex-col items-center justify-center px-6"
      style={{
        transition: 'opacity 0.5s ease-out',
        opacity: stage === 'fading' ? 0 : 1,
      }}
    >
      {/* Background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#D4AF37] rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
          />
        ))}
      </div>

      {/* Title - animates on reveal */}
      <div className="text-center mb-8 relative z-10">
        <div className="relative overflow-hidden" style={{ minHeight: '2.5rem' }}>
          {/* Welcome text - fades out */}
          <h1
            className="font-heading text-3xl transition-all duration-700"
            style={{
              opacity: stage === 'hidden' || stage === 'appearing' ? 1 : 0,
              transform: stage === 'hidden' || stage === 'appearing' ? 'translateY(0)' : 'translateY(-100%)',
              position: stage === 'hidden' || stage === 'appearing' ? 'relative' : 'absolute',
              width: '100%',
              color: '#F5F5F7',
            }}
          >
            {tr('welcome', language)}
          </h1>
          {/* Rarity text - fades in */}
          <h1
            className="font-heading text-3xl font-bold transition-all duration-700"
            style={{
              opacity: stage !== 'hidden' && stage !== 'appearing' && card ? 1 : 0,
              transform: stage !== 'hidden' && stage !== 'appearing' && card ? 'translateY(0)' : 'translateY(100%)',
              color: card ? (RARITY_COLORS[card.rarity] || '#9CA3AF') : '#F5F5F7',
              textShadow: card ? `0 0 20px ${RARITY_COLORS[card.rarity] || '#9CA3AF'}66` : 'none',
            }}
          >
            {card ? (RARITY_LABELS[card.rarity]?.[language] || RARITY_LABELS[card.rarity]?.en || card.rarity) : ''}
          </h1>
        </div>
        <div className="relative overflow-hidden mt-1" style={{ minHeight: '1.5rem' }}>
          {/* Subtitle - fades out */}
          <p
            className="transition-all duration-700"
            style={{
              opacity: stage === 'hidden' || stage === 'appearing' ? 1 : 0,
              transform: stage === 'hidden' || stage === 'appearing' ? 'translateY(0)' : 'translateY(-100%)',
              position: stage === 'hidden' || stage === 'appearing' ? 'relative' : 'absolute',
              width: '100%',
              color: '#A1A1AA',
            }}
          >
            {tr('intro_lead', language)}
          </p>
          {/* Card title - fades in */}
          <p
            className="text-[#F5F5F7] font-medium transition-all duration-700 delay-100"
            style={{
              opacity: stage !== 'hidden' && stage !== 'appearing' && card ? 1 : 0,
              transform: stage !== 'hidden' && stage !== 'appearing' && card ? 'translateY(0)' : 'translateY(100%)',
            }}
          >
            {card?.title || ''}
          </p>
        </div>
      </div>

      {/* Card Container */}
      <div className="relative z-10 perspective-1000">
        {stage === 'no-cards' ? (
          <div className="w-64 h-96 glass-card rounded-2xl flex flex-col items-center justify-center p-6 text-center">
            <Sparkles className="w-12 h-12 text-[#D4AF37] mb-4" />
            <p className="text-[#A1A1AA] mb-6">{error}</p>
            <button
              onClick={skipOnboarding}
              className="btn-gold px-6 py-3 rounded-xl"
            >
              {tr('continue', language)}
            </button>
          </div>
        ) : (
          <div
            onClick={handleCardClick}
            className={`w-64 h-96 cursor-pointer transition-all duration-700 transform-style-preserve-3d ${
              stage === 'appearing' ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
            } ${
              stage === 'revealed' || stage === 'claiming' || stage === 'done' ? 'rotate-y-180' : ''
            }`}
            style={{
              transformStyle: 'preserve-3d',
              transform: stage === 'revealed' || stage === 'claiming' || stage === 'done' 
                ? 'rotateY(180deg)' 
                : 'rotateY(0deg)',
            }}
          >
            {/* Card Back (face down) */}
            <div
              className={`absolute inset-0 w-full h-full rounded-2xl overflow-hidden backface-hidden ${
                stage === 'hidden' ? 'animate-pulse-gold' : ''
              }`}
              style={{ backfaceVisibility: 'hidden' }}
            >
              <div className="w-full h-full bg-gradient-to-br from-[#1a1a2e] to-[#0C0C14] border-2 border-[#D4AF37]/50 rounded-2xl flex flex-col items-center justify-center">
                <div className="w-24 h-24 mb-4 relative">
                  <div className="absolute inset-0 border-2 border-[#D4AF37] rounded-full animate-spin-slow" />
                  <div className="absolute inset-4 border-2 border-[#D4AF37]/50 rounded-full animate-spin-slow-reverse" />
                  <div className="absolute inset-8 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎨</span>
                  </div>
                </div>
                <p className="text-[#D4AF37] font-heading text-lg">PrivateArt</p>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  {tr('tap_to_reveal', language)}
                </p>
              </div>
            </div>

            {/* Card Front (revealed) - full image */}
            <div
              className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden"
              style={{ 
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              {card ? (
                <div className="w-full h-full border border-[#D4AF37]/30 rounded-2xl overflow-hidden">
                  <img
                    src={card.image_url}
                    alt={card.title || 'Card'}
                    className="w-full h-full object-cover"
                    style={{ filter: 'sepia(0.04) contrast(0.975) brightness(0.97) saturate(0.95)' }}
                  />
                </div>
              ) : (
                <div className="w-full h-full bg-[#0C0C14] rounded-2xl flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Claim Button */}
      {(stage === 'revealed' || stage === 'claiming' || stage === 'done') && card && (
        <div className="mt-8 relative z-10">
          <button
            data-testid="claim-first-card-btn"
            onClick={handleClaim}
            disabled={stage === 'claiming' || stage === 'done'}
            className={`px-8 py-4 rounded-xl font-medium text-lg transition-all flex items-center gap-3 ${
              stage === 'done'
                ? 'bg-[#10B981] text-white'
                : stage === 'claiming'
                ? 'bg-[#D4AF37]/50 text-[#05050A] cursor-wait'
                : 'btn-gold animate-pulse-gold'
            }`}
          >
            {stage === 'done' ? (
              <>
                <span>✓</span>
                <span>{tr('claimed', language)}</span>
              </>
            ) : stage === 'claiming' ? (
              <>
                <div className="w-5 h-5 border-2 border-[#05050A] border-t-transparent rounded-full animate-spin" />
                <span>{tr('claiming', language)}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>{tr('claim_painting', language)}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Tap hint for hidden state */}
      {stage === 'hidden' && (
        <p className="mt-8 text-[#A1A1AA] text-sm animate-pulse relative z-10">
          {tr('tap_painting_hint', language)}
        </p>
      )}

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 6s linear infinite;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
      `}</style>
    </div>
  );
};

export default OnboardingCard;
